import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useReducer,
  useMemo,
  useCallback,
} from "react";
import { ContentEditable } from "../contenteditable";
import {
  BACKSPACE,
  DELETE,
  DOWN,
  ENTER,
  LEFT,
  RIGHT,
  UP,
} from "@wordpress/keycodes";
import getVideoId from "get-video-id";
import "./richtext.scss";
import {
  getCaretPosition,
  isCaretStartOrEnd,
  moveCaretPosition,
  pasteHtmlAtCaret,
  pasteTextAtCaret,
  setEndOfContenteditable,
} from "../helpers/richtext/caret-position";
import { sanitizeConf } from "../helpers/richtext/sanitize";
import sanitizeHtml from "sanitize-html";
import { GrigoraDashboardContext } from "../dashboard/context";
import { blockKeywords } from "../blocks";
import {
  getFilesFromDataTransfer,
  isJsonString,
  modulus,
  removeCharsetMetaTag,
  removeWindowsFragments,
  shouldDismissPastedFiles,
} from "../helpers";
import { rawPasteFilesHandler, rawPasteHandler } from "../raw-parser";
import { GrigoraFormatsContext } from "../formats";
import { useRefCallback } from "../hooks";
import {
  documentFragmentToHTML,
  documentFragmentToString,
  emitOnInput,
  getAllTextNodes,
  getAnchors,
  getKeyPressCharacter,
  hasAnchors,
  isMultipleRichTextSelected,
  replaceSelectedHtml,
  splitNode,
  unwrapAnchors,
} from "../dom";
import validUrl from "valid-url";
import { ROOT_NODE, useEditor } from "@craftjs/core";
import { isMac } from "../utils";
import { toast } from "react-toastify";
import { AnimatePresence, motion } from "framer-motion";
import blockActions from "../block-actions";
import { REGEX_FORWARDSLASH, REGEX_FORWARDWORD } from "../regex";

function removeEmptyTags(clean) {
  var treeWalker = document.createTreeWalker(clean, NodeFilter.SHOW_ELEMENT);
  var currentNode = treeWalker.currentNode;
  var emptyNodes = [];

  // test if a node has no text, regardless of whitespaces
  var isNodeEmpty = (node) => !node.textContent.trim();

  var ALLOWED_EMPTY = ["BR", "HR", "IMG", "INPUT"];

  // find all empty nodes
  while (currentNode) {
    // Skip empty
    if (ALLOWED_EMPTY.includes(currentNode.nodeName)) {
      currentNode = treeWalker.nextNode();
      continue;
    }

    isNodeEmpty(currentNode) && emptyNodes.push(currentNode);
    currentNode = treeWalker.nextNode();
  }

  // remove found empty nodes
  emptyNodes.forEach(
    (node) => node.parentNode && node.parentNode.removeChild(node),
  );

  return clean;
}

function detectEmbedContent(link) {
  try {
    try {
      const videoInfo = getVideoId(link);
      if (videoInfo && videoInfo.service === "youtube") {
        return {
          blockSlug: "grigora/youtube",
          blockProps: {
            url: link,
          },
        };
      }
    } catch (error) {
      console.log(error);
    }

    // Check if link is a twitter link or x.com link
    if (link.includes("twitter.com") || link.includes("x.com")) {
      return {
        blockSlug: "grigora/twitter",
        blockProps: {
          remoteTweetURL: link,
        },
      };
    }
    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
}

function getValidTextFromHTML(html) {
  const aux = document.createElement("div");
  aux.innerHTML = html; //parses the html
  return removeEmptyTags(aux)?.innerHTML ?? "";
}

function isValidTypableCharacter(keyCode) {
  var keycode = keyCode;

  var valid =
    (keycode > 47 && keycode < 58) || // number keys
    keycode == 32 ||
    keycode == 13 || // spacebar & return key(s) (if you want to allow carriage returns)
    (keycode > 64 && keycode < 91) || // letter keys
    (keycode > 95 && keycode < 112) || // numpad keys
    (keycode > 185 && keycode < 193) || // ;=,-./` (in order)
    (keycode > 218 && keycode < 223);

  return valid;
}

export function RichTextInput(props) {
  const {
    value = "",
    disabled = false,
    onChange,
    tagName = "p",
    placeholder = "Text...",
    onSplit,
    onMerge,
    noFormatting = false,
    allowedSuggestion,
    onSuggestionCreateBlock,
    selected,
    onPasteParse,
    onEnter,
    linkNotAllowed = false,
    onLinkPaste,
  } = props;

  const { query, actions, enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
    state: state,
  }));

  const {
    editorDocument = document,
    screen,
    editorWindow = window,
  } = useContext(GrigoraDashboardContext) ?? {};

  const isPostEditor = screen !== "site-editor";

  const FormatsContext = useContext(GrigoraFormatsContext);
  const { applyFormat } = FormatsContext ? FormatsContext : {};

  const [suggestionIndex, setSuggestionIndex] = useState(0);

  const ref = useRef();

  const ACTION = useRef();
  const CARET = useRef();

  const validateKeyword = useCallback(
    (array) => {
      const value = ref.current?.innerHTML || "";

      if (!value) {
        return false;
      }

      if (!array) {
        return false;
      }

      // Check if value is a keyword
      var keyword = array.findIndex((item) =>
        item.toLowerCase().includes(value.slice(1).toLowerCase()),
      );

      if (keyword !== -1) {
        return {
          priority: keyword,
        };
      }

      return false;
    },
    [ref.current],
  );

  const getSuggestedBlocks = useCallback(() => {
    var blocks = Object.keys(blockKeywords).map((item) => ({
      block: item,
      isMatch: validateKeyword(blockKeywords[item].keywords),
    }));

    // Filter existing isMatch
    blocks = blocks.filter((item) => item.isMatch);

    // Sort by isMatch.priority
    blocks = blocks.sort((a, b) => {
      return a.isMatch.priority - b.isMatch.priority;
    });

    // Convert array of objects to array of strings
    blocks = blocks.map((item) => item.block);

    return blocks;
  }, [validateKeyword]);

  const suggestedBlocks = useMemo(() => {
    if (!allowedSuggestion) return [];
    return getSuggestedBlocks();
  }, [allowedSuggestion, value]);

  const incrementSuggestionIndex = () => {
    setSuggestionIndex((prev) => modulus(prev + 1, suggestedBlocks.length));
  };

  const decrementSuggestionIndex = () => {
    setSuggestionIndex((prev) => modulus(prev - 1, suggestedBlocks.length));
  };

  useEffect(() => {
    ref?.current?.addEventListener("grigorapaste", pasteHandlerHTML);

    return () => {
      ref?.current?.removeEventListener("grigorapaste", pasteHandlerHTML);
    };
  }, [ref.current]);

  useEffect(() => {
    if (ACTION.current === "MERGE_FORWARD") {
      moveCaretPosition(editorDocument, CARET.current);
    }
    ACTION.current = "";
  }, [value]);

  const handleOnKeyDown = useRefCallback(
    (e) => {
      const value = ref.current?.innerHTML || "";

      const currentCaretStartEnd = isCaretStartOrEnd(
        editorDocument,
        e.currentTarget,
      );

      const ctrlKey = isMac() ? e.metaKey : e.ctrlKey;

      // Open Link
      if (e.altKey) {
        // Open Link
        if (e.keyCode === 13) {
          var selection = editorDocument.getSelection();
          var range = selection.getRangeAt(0);
          var element = range.commonAncestorContainer;
          if (element) {
            var anchor = getParentsUntil(
              element,
              "a",
              ".grigora-richtext-input__field",
            );
            if (anchor) {
              e.preventDefault();
              e.stopPropagation();
              var link = anchor.attr("href");
              window.open(link, "_blank");
            }
          }
        }
      }

      if (e.keyCode === ENTER && (e.shiftKey || ctrlKey)) {
      }
      // Explicity defined onEnter action
      else if (e.keyCode === ENTER) {
        if (onEnter) {
          onEnter();
          e.preventDefault();
          return;
        }
        // Add new Block if Suggestion Box is open.
        var filteredArr = getSuggestedBlocks();
        if (suggestionOpened(value) && filteredArr.length > 0) {
          e.preventDefault();
          onSuggestionCreateBlock(
            filteredArr[modulus(suggestionIndex, filteredArr.length)],
          );
        }

        // Split Check
        else if (onSplit) {
          e.preventDefault();

          if (e.currentTarget.innerHTML && e.currentTarget.innerHTML.trim()) {
            let ranges = splitNode(
              editorDocument,
              editorDocument.getSelection(),
              e.currentTarget,
            );
            let prevFragment = ranges.previous.cloneContents();
            let nextFragment = ranges.next.cloneContents();

            prevFragment =
              documentFragmentToString(editorDocument, prevFragment) === ""
                ? ""
                : documentFragmentToHTML(editorDocument, prevFragment);

            nextFragment =
              documentFragmentToString(editorDocument, nextFragment) === ""
                ? ""
                : documentFragmentToHTML(editorDocument, nextFragment);

            let historyMergeAction = actions.history.merge();

            onChange(prevFragment.trim(), historyMergeAction);
            onSplit(nextFragment, prevFragment.trim(), historyMergeAction);
          } else {
            onSplit("");
          }
        }
      }
      // If whole text is selected
      else if (
        currentCaretStartEnd.atStart &&
        currentCaretStartEnd.atEnd &&
        editorDocument.getSelection().toString() !== ""
      ) {
      }
      // On Merge Back
      else if (
        !ctrlKey &&
        currentCaretStartEnd.atStart &&
        e.keyCode === BACKSPACE &&
        onMerge &&
        editorDocument.getSelection().toString() === ""
      ) {
        ACTION.current = "MERGE_BACKWARD";
        e.preventDefault();
        onMerge(false);
      }
      // On Merge Forward
      else if (
        !ctrlKey &&
        currentCaretStartEnd.atEnd &&
        !e.ctrlKey &&
        e.keyCode === DELETE &&
        onMerge &&
        editorDocument.getSelection().toString() === ""
      ) {
        ACTION.current = "MERGE_FORWARD";
        CARET.current = getCaretPosition(editorDocument);
        e.preventDefault();
        onMerge(true);
      }
      // On Navigate Next
      else if (currentCaretStartEnd.atEnd && e.keyCode === RIGHT) {
        e.preventDefault();
        var allRTs = editorDocument.querySelectorAll(
          ".grigora-richtext-input .grigora-richtext-input__field",
        );
        let found = false;
        for (var i = 0; i < allRTs.length; i++) {
          if (allRTs[i].isSameNode(e.currentTarget)) {
            found = i;
            break;
          }
        }
        if (found === false) {
          return;
        }
        if (found < allRTs.length - 1) {
          allRTs[found + 1].focus();
        }
      }
      // On Navigate Back
      else if (currentCaretStartEnd.atStart && e.keyCode === LEFT) {
        e.preventDefault();
        var allRTs = editorDocument.querySelectorAll(
          ".grigora-richtext-input .grigora-richtext-input__field",
        );
        let found = false;
        for (var i = 0; i < allRTs.length; i++) {
          if (allRTs[i].isSameNode(e.currentTarget)) {
            found = i;
            break;
          }
        }
        if (found === false) {
          return;
        }
        if (found > 0) {
          allRTs[found - 1].focus();
          setEndOfContenteditable(editorDocument, allRTs[found - 1]);
        }
      } else if (e.keyCode === UP) {
        if (suggestionOpened(value)) {
          // Don't Move Caret if suggestion box is open.
          e.preventDefault();
          e.stopPropagation();
          decrementSuggestionIndex();
        }

        // Move Caret Up - TODO
        // moveCaretUpOrDown(ref.current, -1)
        // e.preventDefault();
      } else if (e.keyCode === DOWN) {
        if (suggestionOpened(value)) {
          // Don't Move Caret if suggestion box is open.
          e.preventDefault();
          e.stopPropagation();
          incrementSuggestionIndex();
        }

        // Move Caret Down
      }
      // Handle the exception of last word not being deleted when done double click select on it.
      // Hence we're adding manually adding cases

      // If its post editor we have to manually handle the keypress for selection
      if (isPostEditor) {
        const ctrlKey = isMac() ? e.metaKey : e.ctrlKey;

        // Skip the following if richtext is not in grigora-editor-renderer-canvas
        if (!e.currentTarget.closest(".grigora-editor-renderer-canvas")) {
          return;
        }

        if (ctrlKey) {
          // Select all and if richttext is empty
          if (e.keyCode === 65 && !value) {
            // Select all richtext
            // Find all richtexts and select from top to bottom

            // Set canvas contenteditable to true
            const canvas = editorDocument.querySelector(
              ".grigora-editor-renderer-canvas",
            );

            if (canvas) {
              canvas.contentEditable = true;
            }

            var allRTs = editorDocument.querySelectorAll(
              ".grigora-editor-renderer-canvas .grigora-richtext-input .grigora-richtext-input__field",
            );

            if (allRTs.length > 0) {
              var range = editorDocument.createRange().cloneRange();
              var textNodesInFirst = getAllTextNodes(allRTs[0]);
              var textNodesInLast = getAllTextNodes(allRTs[allRTs.length - 1]);
              range.setStart(textNodesInFirst?.[0] ?? allRTs[0], 0);
              range.setEnd(
                textNodesInLast?.at(-1)
                  ? textNodesInLast?.at(-1)
                  : allRTs[allRTs.length - 1],
                textNodesInLast?.at(-1) ? textNodesInLast?.at(-1).length : 0,
              );
              var sel = editorDocument.getSelection();
              sel.removeAllRanges();
              sel.addRange(range);
            }

            // Select All Nodes if Selection Change is not triggered
            blockActions.selectAllChildrens(actions, query, ROOT_NODE);
          }

          // Select all if all the richtext is selected and this is triggered by mistake if canvas contenteditable is not set true
          if (
            e.keyCode === 65 &&
            value &&
            currentCaretStartEnd.atStart &&
            currentCaretStartEnd.atEnd
          ) {
            // Select all richtext
            // Find all richtexts and select from top to bottom

            // Set canvas contenteditable to true
            const canvas = editorDocument.querySelector(
              ".grigora-editor-renderer-canvas",
            );

            if (canvas) {
              canvas.contentEditable = true;
            }

            var allRTs = editorDocument.querySelectorAll(
              ".grigora-editor-renderer-canvas .grigora-richtext-input .grigora-richtext-input__field",
            );

            if (allRTs.length > 0) {
              var range = editorDocument.createRange().cloneRange();
              var textNodesInFirst = getAllTextNodes(allRTs[0]);
              var textNodesInLast = getAllTextNodes(allRTs[allRTs.length - 1]);
              range.setStart(textNodesInFirst?.[0] ?? allRTs[0], 0);
              range.setEnd(
                textNodesInLast?.at(-1)
                  ? textNodesInLast?.at(-1)
                  : allRTs[allRTs.length - 1],
                textNodesInLast?.at(-1) ? textNodesInLast?.at(-1).length : 0,
              );
              var sel = editorDocument.getSelection();
              sel.removeAllRanges();
              sel.addRange(range);
            }

            // Select All Nodes if Selection Change is not triggered
            blockActions.selectAllChildrens(actions, query, ROOT_NODE);
          }
          // Copy
          if (e.keyCode === 67) {
            if (!isPostEditor) {
              e.preventDefault();
            }

            return;
          }
          // Cut
          else if (e.keyCode === 88) {
            e.preventDefault();
            return;
          }
          // // Select All text
          // else if (e.keyCode === 65) {
          //   e.preventDefault();
          //   return;
          // }
        }

        if (ctrlKey) {
          return;
        }

        if (editorDocument.getSelection().toString() !== "") {
          if (
            !isValidTypableCharacter(e.keyCode) &&
            // If is delete or backspace, dont return
            !(e.keyCode === BACKSPACE || e.keyCode === DELETE)
          ) {
            return;
          }

          e.stopPropagation();
          e.preventDefault();

          // This is triggered when selection is selected and keypress is done.
          // This gets triggered instead of RichText onKeyDown. So, we need to trigger richtext event using this.

          const selection = editorDocument.getSelection();
          // If multiple richtext is selected
          if (isMultipleRichTextSelected(selection)) {
            return;
          }

          // Check if selection is from richtext instance
          var currentEditableRichText = ref.current;
          // is not within the contenteditable div
          if (!currentEditableRichText) {
            return;
          }

          var character = getKeyPressCharacter(e);

          // Delete the selection text
          replaceSelectedHtml(selection, editorDocument, character, true);

          // Emit onchange
          emitOnInput(ref.current);
        }
      }
    },
    [
      ref.current,
      onSplit,
      onMerge,
      onEnter,
      editorDocument,
      onSuggestionCreateBlock,
      suggestionIndex,
      getSuggestedBlocks,
      validateKeyword,
      isPostEditor,
    ],
  );

  const pasteHandlerHTML = useRefCallback(
    (e) => {
      // toast.loading("Working...", {
      //   toastId: "paste",
      // });
      var {
          detail: {
            html = "",
            plainText = "",
            files = "",
            grigoraBlockJson,
          } = {},
        } = e,
        // Remove Windows-specific metadata appended within copied HTML text.
        html = removeWindowsFragments(html);

      // Strip meta tag.
      html = removeCharsetMetaTag(html);

      if (noFormatting) {
        // add plain text to caret here
        pasteHtmlAtCaret(editorDocument, plainText);
        emitOnInput(ref.current);
        return;
      }

      // If copied data has grigora blocks
      if (grigoraBlockJson) {
        if (isJsonString(grigoraBlockJson)) {
          const blocks = JSON.parse(grigoraBlockJson);
          if (onPasteParse) {
            onPasteParse(blocks, true);
          } else {
            if (isPostEditor) {
              blockActions.pasteParsed(
                actions,
                query,
                undefined,
                blocks,
                editorDocument,
                true,
              );
            }
          }
        }
        return;
      }

      // Process any attached files, unless we infer that the files in
      // question are redundant "screenshots" of the actual HTML payload,
      // as created by certain office-type programs.
      if (files?.length && !shouldDismissPastedFiles(files, html, plainText)) {
        const content = rawPasteFilesHandler(files);
        onPasteParse && onPasteParse(content);
        return;
      }

      var content = rawPasteHandler(html, plainText, tagName, !onPasteParse);

      var selectedBlocks = query.getEvent("selected").all() ?? [];

      if (typeof content === "string" && selectedBlocks.length > 1) {
        content = [
          {
            blockSlug: "grigora/text",
            blockProps: {
              content: content,
            },
          },
        ];
      }

      // Here check if the content is of type embed then convert to respective embed blocks
      // Supported embeds are youtube, and twitter
      // If its youtube then convert to youtube block
      // If its twitter then convert to twitter block
      const embedContent = detectEmbedContent(content);
      console.log(embedContent);

      if (embedContent && editorDocument.getSelection().toString() === "") {
        content = [
          {
            blockSlug: embedContent.blockSlug,
            blockProps: embedContent.blockProps,
          },
        ];
      }

      if (typeof content === "string") {
        if (
          validUrl.isUri(content) &&
          editorDocument.getSelection().toString() !== ""
        ) {
          if (content) {
            if (!linkNotAllowed) {
              applyFormat("link", {
                href: content,
              });
            } else {
              onLinkPaste &&
                onLinkPaste({
                  href: content,
                });
              content = "";
            }
          }
          return;
        } else if (
          validUrl.isUri(content) &&
          editorDocument.getSelection().toString() === ""
        ) {
          // Transform content to link <a> tag
          if (!linkNotAllowed) {
            content = `<a href="${content}">${content}</a>`;
          } else {
            onLinkPaste &&
              onLinkPaste({
                href: content,
              });
            content = "";
          }
        }

        if (linkNotAllowed && hasAnchors(content)) {
          var anchors = getAnchors(content);
          content = unwrapAnchors(content);

          onLinkPaste && onLinkPaste(anchors[0]);
        }

        if (content) {
          if (tagName === "code") {
            pasteTextAtCaret(editorDocument, content);
          } else {
            pasteHtmlAtCaret(editorDocument, content);
          }
          emitOnInput(ref.current);
        }
      } else if (content.length > 0) {
        if (onPasteParse) {
          onPasteParse(content, false, selectedBlocks.length > 1);
        } else {
          // Paste in current richtext if is of type text block and only one block
          if (
            selectedBlocks.length === 1 &&
            content.length === 1 &&
            content[0].blockSlug === "grigora/text"
          ) {
            var contentToPaste = content[0].blockProps.content;
            var anchors = [];

            if (contentToPaste) {
              // Sanitize without links
              if (!linkNotAllowed) {
                pasteHtmlAtCaret(editorDocument, contentToPaste);
                emitOnInput(ref.current);
              } else {
                if (hasAnchors(contentToPaste)) {
                  anchors = getAnchors(contentToPaste);
                  contentToPaste = unwrapAnchors(contentToPaste);
                }

                if (anchors.length > 0 && anchors[0].href) {
                  onLinkPaste && onLinkPaste(anchors[0]);
                }
              }
            }

            return;
          }
        }
      }
    },
    [
      editorDocument,
      noFormatting,
      onPasteParse,
      onLinkPaste,
      linkNotAllowed,
      tagName,
    ],
  );

  const pasteHandler = useRefCallback(
    (event) => {
      // if (!onPasteParse) {
      //   return;
      // }
      const { clipboardData } = event;

      let plainText = "";
      let html = "";

      // IE11 only supports `Text` as an argument for `getData` and will
      // otherwise throw an invalid argument error, so we try the standard
      // arguments first, then fallback to `Text` if they fail.
      try {
        plainText = clipboardData.getData("text/plain");
        html = clipboardData.getData("text/html");
      } catch (error1) {
        try {
          html = clipboardData.getData("Text");
        } catch (error2) {
          // Some browsers like UC Browser paste plain text by default and
          // don't support clipboardData at all, so allow default
          // behaviour.
          return;
        }
      }

      event.preventDefault();
      event.stopPropagation();

      const files = [...getFilesFromDataTransfer(clipboardData)];

      const grigoraBlockJson = clipboardData.getData("grigora/blocks");

      pasteHandlerHTML({
        detail: { html, plainText, files, grigoraBlockJson },
      });
    },
    [pasteHandlerHTML],
  );

  const handleOnChange = useRefCallback(
    (e) => {
      if (noFormatting) {
        onChange(e.currentTarget.innerText || e.currentTarget.textContent);
        return;
      }

      // Remove Empty br tags for firefox browser exception
      if (e.target.value === "<br>") {
        onChange("");
        return;
      }

      onChange(e.target.value);
    },
    [noFormatting],
  );

  const handleBlur = useRefCallback(
    (e) => {
      // No formatting Blur

      if (noFormatting) {
        onChange(e.currentTarget.innerText || e.currentTarget.textContent);
        e.currentTarget.innerHTML =
          e.currentTarget.innerText || e.currentTarget.textContent;
        return;
      }

      var old = e.currentTarget.innerHTML;
      var currentValue = e.currentTarget.innerHTML;

      if (old === "<br>") {
        onChange("");
        return;
      }

      // Remove zero width characters if added by any format. The character used is - &#8203;
      old = old.replace && old.replace(/\u200B/g, "");

      // Remove empty tags
      old = getValidTextFromHTML(old);

      // Replace nbsp
      // old = old.replace && old.replace(/&nbsp;/g, " ");
      // Remove not allowed tags.
      // old = sanitizeHtml(old, sanitizeConf);

      if (old !== currentValue) {
        onChange(old);
      }
    },
    [noFormatting],
  );

  const modifyMultiRichtextSelection = useCallback(() => {
    var selection = editorDocument.getSelection();
    var range = selection.getRangeAt(0).cloneRange();

    if (!ref.current) return;

    // if (isMultipleRichTextSelected(selection)) {
    var allTextNodes = getAllTextNodes(ref.current);
    var firstChild = allTextNodes[0];
    var lastChild = allTextNodes.at(-1);
    if (firstChild) {
      range.setStart(firstChild, 0);
    }
    if (lastChild) {
      range.setEnd(lastChild, lastChild.length);
    }
    selection.removeAllRanges();
    selection.addRange(range);
  }, [editorDocument, ref.current]);

  const handleClick = useRefCallback(
    (e) => {
      // To Prevent selecting two blocks in triple click
      if (e.detail === 2) {
        var selection = editorDocument.getSelection();
        var range = selection.getRangeAt(0).cloneRange();
      } else if (e.detail >= 3) {
        modifyMultiRichtextSelection();
      }
    },
    [editorDocument, modifyMultiRichtextSelection],
  );

  const suggestionOpened = useCallback(
    (value) => {
      const slash = new RegExp(REGEX_FORWARDSLASH);
      const word = new RegExp(REGEX_FORWARDWORD);
      if (
        allowedSuggestion &&
        selected &&
        slash.test(value) &&
        word.test(value)
      ) {
        return true;
      }
      return false;
    },
    [allowedSuggestion, selected],
  );

  useEffect(() => {
    editorDocument
      ?.querySelector(".blocks-suggestion .active")
      ?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
  }, [suggestionIndex]);

  return (
    <>
      <div className="grigora-richtext-input">
        <ContentEditable
          innerRef={ref}
          // role="textbox"
          html={value}
          disabled={disabled || !enabled}
          onChange={handleOnChange}
          onKeyDown={handleOnKeyDown}
          tagName={tagName}
          placeholder={placeholder ? placeholder : "Text..."}
          className="grigora-richtext-input__field"
          tabIndex="0"
          onBlur={handleBlur}
          onPaste={pasteHandler}
          onClick={handleClick}
          // data-gramm_editor={false}
        />
        <AnimatePresence>
          {suggestionOpened(value) ? (
            <>
              <motion.div
                // eslint-disable-next-line react/no-unknown-property
                onLoad={() => {
                  editorDocument
                    ?.querySelector(".blocks-suggestion")
                    ?.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                      inline: "center",
                    });
                }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.1 }}
                style={{ originX: 0, originY: 0, userSelect: "none" }}
                className="blocks-suggestion"
                contentEditable={false}
              >
                <div className="container">
                  {suggestedBlocks.length > 0 ? (
                    suggestedBlocks.map((item, i, filteredArr) => (
                      <div
                        key={"rt-suggestion-" + item}
                        onClick={() => {
                          onSuggestionCreateBlock(item, {}, true);
                        }}
                        className={`single-option ${
                          i === modulus(suggestionIndex, filteredArr.length)
                            ? "active"
                            : ""
                        }`}
                      >
                        {blockKeywords[item].icon ? (
                          <div className="icon">{blockKeywords[item].icon}</div>
                        ) : null}
                        {blockKeywords[item].label}
                      </div>
                    ))
                  ) : (
                    <div className="no-results">No Results</div>
                  )}
                </div>
              </motion.div>
            </>
          ) : null}
        </AnimatePresence>
      </div>
    </>
  );
}
export default RichTextInput;
